import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  MessageOutlined,
  HistoryOutlined,
  PlusOutlined,
  InboxOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { getTariffItemRowKey } from './tariffRowKeys';

const { Title, Text } = Typography;
const { TextArea } = Input;

const getServerMessage = (resp, fallback) => (resp?.data?.message != null && resp.data.message !== '') ? resp.data.message : fallback;
const getServerError = (e, fallback) => (e?.response?.data?.message != null && e.response.data.message !== '') ? e.response.data.message : (e?.message || fallback);

const featureEnabled =
  process.env.REACT_APP_TARIFF_FEATURE_ENABLED === '1' ||
  process.env.REACT_APP_TARIFF_FEATURE_ENABLED === 'true';

const StatusTag = ({ status }) => {
  if (!status) return <Tag>unknown</Tag>;
  let color = 'default';
  if (status === 'pending_review' || status === 'submitted') color = 'gold';
  else if (status === 'under_review') color = 'blue';
  else if (status === 'approved' || status === 'active') color = 'green';
  else if (status === 'rejected') color = 'red';
  else if (status === 'counter_proposed' || status === 'needs_revision') color = 'purple';
  return <Tag color={color}>{String(status)}</Tag>;
};

const TERMINAL_STATUSES = ['approved', 'rejected', 'active'];

// Humanize action type for the story
const humanizeAction = (action) => {
  if (!action) return 'Updated';
  const s = String(action).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

// Build a short story narrative from a history entry
const getHistoryStory = (h) => {
  const action = humanizeAction(h.action_type ?? h.action ?? 'update');
  const actor = h.actor ?? h.user_id ?? '';
  const dateStr = h.created_at ? new Date(h.created_at).toLocaleString() : '';
  const desc = h.description ?? h.message ?? '';
  const oldV = h.old_values && typeof h.old_values === 'object' ? h.old_values : null;
  const newV = h.new_values && typeof h.new_values === 'object' ? h.new_values : null;

  const parts = [];
  if (dateStr) parts.push(`On ${dateStr}`);
  if (actor) parts.push(`${actor}`);

  let story = '';
  if (oldV && newV) {
    const keys = [...new Set([...Object.keys(oldV), ...Object.keys(newV)])].filter((k) => oldV[k] !== newV[k]);
    if (keys.length) {
      const changePhrases = keys.map((k) => {
        const from = oldV[k] ?? '—';
        const to = newV[k] ?? '—';
        const label = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return `${label} was changed from ${String(from)} to ${String(to)}`;
      });
      story = changePhrases.join('. ');
    }
  }
  if (!story) story = `${action} was performed`;

  const who = parts.length ? ` by ${parts.filter(Boolean).join(' · ')}` : '';
  let full = `${story}${who}.`;
  if (desc) full += ` ${desc}`;
  return full;
};

// One-line label for collapse header
const getHistoryStoryLabel = (h) => {
  const action = humanizeAction(h.action_type ?? h.action ?? 'update');
  const dateStr = h.created_at ? new Date(h.created_at).toLocaleString() : '';
  const actor = h.actor ?? h.user_id ?? '';
  if (dateStr && actor) return `${action} · ${dateStr} · ${actor}`;
  if (dateStr) return `${action} · ${dateStr}`;
  return action;
};

const CounterProposeModal = ({
  visible,
  tariffId,
  loading,
  onClose,
  onSubmit,
  onPercentageSubmit,
  categoryOptions = [],
}) => {
  const [activeTab, setActiveTab] = useState('items');
  const [messageText, setMessageText] = useState('');
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(20);
  const [modalItems, setModalItems] = useState([]);
  const [modalTotal, setModalTotal] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);
  const [adjustmentsMap, setAdjustmentsMap] = useState({});
  const [percentLoading, setPercentLoading] = useState(false);
  const [percentForm, setPercentForm] = useState({
    percentage: 10,
    direction: 'increase',
    base_field: 'facility_cost',
    rounding_mode: 'nearest_naira',
    search: '',
    category_id: undefined,
    negotiation_status: undefined,
    message: '',
  });

  const loadModalItems = useCallback(async () => {
    if (!tariffId || !visible) return;
    setModalLoading(true);
    try {
      const resp = await lib.getAdminTariffItems(tariffId, {
        page: modalPage,
        page_size: modalPageSize,
      });
      const body = resp?.data;
      const data = body?.data ?? body;
      const list = data?.items ?? (Array.isArray(data) ? data : []);
      setModalItems(Array.isArray(list) ? list : []);
      setModalTotal(data?.total ?? 0);
    } catch {
      setModalItems([]);
      setModalTotal(0);
    } finally {
      setModalLoading(false);
    }
  }, [tariffId, visible, modalPage, modalPageSize]);

  useEffect(() => {
    if (visible && tariffId) {
      setAdjustmentsMap({});
      setMessageText('');
      setModalPage(1);
    }
  }, [visible, tariffId]);

  useEffect(() => {
    if (visible && tariffId && activeTab === 'items') {
      loadModalItems();
    }
  }, [visible, tariffId, activeTab, modalPage, modalPageSize, loadModalItems]);

  const updateCost = (itemId, value) => {
    setAdjustmentsMap((prev) => ({ ...prev, [itemId]: value != null ? Number(value) : 0 }));
  };

  const getCostForRow = (record) => {
    const id = record.id ?? record.facility_tariff_item_id;
    if (adjustmentsMap.hasOwnProperty(id)) return adjustmentsMap[id];
    return record.counter_proposed_hmo_cost ?? record.proposed_hmo_cost ?? record.facility_cost ?? 0;
  };

  const handleSubmitItems = () => {
    const adjustments = Object.entries(adjustmentsMap)
      .filter(([, v]) => v != null && Number(v) >= 0)
      .map(([facility_tariff_item_id, proposed_hmo_cost]) => ({
        facility_tariff_item_id: Number(facility_tariff_item_id),
        proposed_hmo_cost: Number(proposed_hmo_cost),
      }));
    onSubmit(adjustments, messageText);
  };

  const handleSubmitPercentage = async () => {
    const { percentage, direction, base_field, rounding_mode, search, category_id, negotiation_status, message } = percentForm;
    if (!percentage || percentage <= 0) {
      notification.warning({ message: 'Enter a positive percentage', placement: 'topRight' });
      return;
    }
    setPercentLoading(true);
    try {
      const payload = {
        percentage: Number(percentage),
        direction,
        base_field,
        rounding_mode,
        message: message.trim() || undefined,
      };
      if (search) payload.search = search;
      if (category_id != null) payload.category_id = category_id;
      if (negotiation_status) payload.negotiation_status = negotiation_status;
      await onPercentageSubmit(payload);
      onClose();
    } catch (e) {
      notification.error({ message: getServerError(e, 'Percentage apply failed'), placement: 'topRight' });
    } finally {
      setPercentLoading(false);
    }
  };

  const itemTable = (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Optional message to facility</Text>
        <TextArea rows={2} value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="e.g. Please consider our counter-proposal." />
      </div>
      <Table
        size="small"
        dataSource={modalItems}
        rowKey={getTariffItemRowKey}
        loading={modalLoading}
        scroll={{ y: 240 }}
        pagination={{
          current: modalPage,
          pageSize: modalPageSize,
          total: modalTotal,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (t) => `Total ${t} item(s)`,
          onChange: (p, size) => {
            setModalPage(p);
            setModalPageSize(size || 20);
          },
        }}
        columns={[
          { title: 'Item Name', dataIndex: 'item_name', key: 'n', width: 160, ellipsis: true },
          { title: 'Strength', dataIndex: 'item_strength', key: 'st', width: 90, ellipsis: true, render: (v) => (v && String(v).trim()) || '—' },
          { title: 'Presentation', dataIndex: 'item_presentation', key: 'pr', width: 110, ellipsis: true, render: (v) => (v && String(v).trim()) || '—' },
          { title: 'Facility cost', dataIndex: 'facility_cost', key: 'fc', width: 100, render: (v) => (v != null ? Number(v) : '—') },
          {
            title: 'HMO cost',
            key: 'hmo',
            width: 120,
            render: (_, record) => {
              const itemId = record.id ?? record.facility_tariff_item_id;
              return (
                <InputNumber
                  size="small"
                  min={0}
                  value={getCostForRow(record)}
                  onChange={(val) => updateCost(itemId, val)}
                />
              );
            },
          },
        ]}
      />
    </Space>
  );

  const percentFormUI = (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Percentage (required)</Text>
        <InputNumber min={0.01} max={100} step={1} value={percentForm.percentage} onChange={(v) => setPercentForm((p) => ({ ...p, percentage: v }))} style={{ width: 120 }} />
      </div>
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Direction</Text>
        <Select
          value={percentForm.direction}
          onChange={(v) => setPercentForm((p) => ({ ...p, direction: v }))}
          options={[
            { value: 'increase', label: 'Increase' },
            { value: 'decrease', label: 'Decrease' },
          ]}
          style={{ width: 160 }}
        />
      </div>
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Base field</Text>
        <Select
          value={percentForm.base_field}
          onChange={(v) => setPercentForm((p) => ({ ...p, base_field: v }))}
          options={[
            { value: 'facility_cost', label: 'Facility cost' },
            { value: 'hmo_cost', label: 'HMO cost' },
            { value: 'proposed_hmo_cost', label: 'Proposed HMO cost' },
          ]}
          style={{ width: 200 }}
        />
      </div>
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Rounding</Text>
        <Select
          value={percentForm.rounding_mode}
          onChange={(v) => setPercentForm((p) => ({ ...p, rounding_mode: v }))}
          options={[
            { value: 'nearest_naira', label: 'Nearest naira' },
            { value: 'two_dp', label: 'Two decimal places' },
            { value: 'none', label: 'None' },
          ]}
          style={{ width: 200 }}
        />
      </div>
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Filters (optional)</Text>
        <Space wrap>
          <Input
            placeholder="Search"
            value={percentForm.search}
            onChange={(e) => setPercentForm((p) => ({ ...p, search: e.target.value }))}
            style={{ width: 160 }}
            allowClear
          />
          <Select
            placeholder="Category"
            value={percentForm.category_id}
            onChange={(v) => setPercentForm((p) => ({ ...p, category_id: v }))}
            style={{ width: 160 }}
            allowClear
            options={categoryOptions}
            loading={!categoryOptions.length && visible}
          />
          <Select
            placeholder="Negotiation status"
            value={percentForm.negotiation_status}
            onChange={(v) => setPercentForm((p) => ({ ...p, negotiation_status: v }))}
            style={{ width: 180 }}
            allowClear
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'pending_negotiation', label: 'Pending negotiation' },
              { value: 'approved', label: 'Approved' },
              { value: 'proposed', label: 'Proposed' },
            ]}
          />
        </Space>
      </div>
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Message to facility (optional)</Text>
        <TextArea rows={2} value={percentForm.message} onChange={(e) => setPercentForm((p) => ({ ...p, message: e.target.value }))} placeholder="e.g. Applied +10% on facility costs." />
      </div>
      <Button type="primary" loading={percentLoading} onClick={handleSubmitPercentage}>
        Apply percentage to matching items
      </Button>
    </Space>
  );

  return (
    <Modal
      title="Counter-propose"
      open={visible}
      width={720}
      footer={activeTab === 'items' ? (
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={handleSubmitItems} disabled={modalLoading}>
            Submit counter-proposal
          </Button>
        </Space>
      ) : null}
      onCancel={onClose}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'items', label: 'By item', children: itemTable },
          { key: 'percent', label: 'By %', children: percentFormUI },
        ]}
      />
    </Modal>
  );
};

const AdminTariffDetail = () => {
  const { tariffId, facilityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tariff, setTariff] = useState(null);
  // Read by loadItems' fallback without making it a dependency: with `tariff`
  // in the deps the items were fetched twice on every page open.
  const tariffRef = useRef(null);
  useEffect(() => { tariffRef.current = tariff; }, [tariff]);
  const [activeTab, setActiveTab] = useState('items');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [itemDetailModalRecord, setItemDetailModalRecord] = useState(null);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [itemsData, setItemsData] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(50);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsSearch, setItemsSearch] = useState('');
  const [itemsCategoryId, setItemsCategoryId] = useState(undefined);
  const [itemsNegotiationStatus, setItemsNegotiationStatus] = useState();
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [addItemLoading, setAddItemLoading] = useState(false);
  const [addItemForm] = Form.useForm();

  const [unreadCount, setUnreadCount] = useState(0);

  const rejectReasonRef = useRef('');
  const revisionMessageRef = useRef('');
  const proposeCostRef = useRef(0);
  const proposeReasonRef = useRef('');

  const id = tariffId || tariff?.id || tariff?.tariff_id;
  const stateTariff = location.state?.tariff;
  const uploadReportPath =
    stateTariff?.upload_report_url ?? stateTariff?.upload_report_path ??
    tariff?.upload_report_url ?? tariff?.upload_report_path ??
    null;
  const hasUploadReport = useMemo(
    () =>
      Boolean(
        uploadReportPath ||
        (tariff && (tariff.has_upload_report || tariff.upload_report_available)) ||
        (stateTariff && (stateTariff.has_upload_report || stateTariff.upload_report_available))
      ),
    [tariff, stateTariff, uploadReportPath]
  );
  const isTerminal = tariff && TERMINAL_STATUSES.includes(tariff.status);

  const isTariffArchived = Boolean(tariff?.tariff_archived) || tariff?.tariff_archived === 1 || tariff?.tariff_archived === '1';
  const [archiveTariffModalOpen, setArchiveTariffModalOpen] = useState(false);
  const [archiveTariffReason, setArchiveTariffReason] = useState('');
  const [archiveTariffBlocking, setArchiveTariffBlocking] = useState(null);
  const [archivingTariff, setArchivingTariff] = useState(false);

  const closeArchiveTariffModal = () => {
    setArchiveTariffModalOpen(false);
    setArchiveTariffBlocking(null);
    setArchiveTariffReason('');
  };

  const runArchiveTariff = async (force = false) => {
    if (!id) return;
    setArchivingTariff(true);
    try {
      const resp = await lib.archiveAdminTariff(id, { reason: archiveTariffReason, force });
      if (resp?.data?.error) {
        if (lib.isTariffArchiveBlocked(resp)) {
          const blocking = resp?.data?.data?.blocking ?? {};
          setArchiveTariffBlocking({
            active_pointer_count: Number(blocking.active_pointer_count) || 0,
            facilities: Array.isArray(blocking.facilities) ? blocking.facilities : [],
          });
          return;
        }
        throw new Error(resp.data.message ?? 'Failed to archive tariff');
      }
      closeArchiveTariffModal();
      await loadTariff();
      notification.success({ message: getServerMessage(resp, 'Tariff archived'), placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Failed to archive tariff'), placement: 'topRight' });
    } finally {
      setArchivingTariff(false);
    }
  };

  const handleUnarchiveTariff = () => {
    if (!id) return;
    Modal.confirm({
      title: 'Restore this tariff?',
      content: 'It will reappear in tariff lists and pickers. This does not by itself make it active for any facility again.',
      okText: 'Restore',
      onOk: async () => {
        try {
          const resp = await lib.unarchiveAdminTariff(id);
          if (resp?.data?.error) throw new Error(resp.data.message ?? 'Failed to restore tariff');
          await loadTariff();
          notification.success({ message: getServerMessage(resp, 'Tariff restored'), placement: 'topRight' });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Failed to restore tariff'), placement: 'topRight' });
        }
      },
    });
  };

  const loadTariff = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await lib.getAdminTariffDetail(id);
      const body = resp?.data;
      if (body?.error) {
        setError((body?.message != null && body.message !== '') ? body.message : 'Unable to load tariff detail');
      } else {
        setTariff(body?.data || body?.tariff || body);
      }
    } catch (e) {
      setError(getServerError(e, 'Network error while loading tariff detail'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!featureEnabled) return;
    if (id) loadTariff();
    // featureEnabled is a build-time flag from process.env and will not change at runtime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadTariff]);

  const loadMessages = useCallback(async () => {
    if (!id) return;
    setMessagesLoading(true);
    try {
      const resp = await lib.getTariffMessages(id);
      const body = resp?.data;
      const list =
        (Array.isArray(body?.data) ? body.data : null)
        ?? (Array.isArray(body?.messages) ? body.messages : null)
        ?? (Array.isArray(body?.items) ? body.items : null)
        ?? (Array.isArray(body?.results) ? body.results : null)
        ?? (Array.isArray(body?.data?.messages) ? body.data.messages : null)
        ?? (Array.isArray(body?.data?.items) ? body.data.items : null)
        ?? (Array.isArray(body) ? body : []);
      const rawList = Array.isArray(list) ? list : [];
      const safeList = rawList.map((m) => ({
        id: m.id ?? m.message_id,
        sender: m.sender ?? m.source ?? m.sender_type ?? m.from ?? (m.message_type === 'query' ? 'hmo' : 'facility'),
        source: m.source ?? m.sender,
        created_at: m.created_at ?? m.createdAt ?? m.sent_at ?? m.sentAt,
        message_text: m.message_text ?? m.text ?? m.content ?? m.body ?? m.message ?? '',
      }));
      setMessages(safeList);

      // Mark messages as read when we load them
      if (safeList.length) {
        try {
          await Promise.all(
            safeList
              .map((m) => m.id)
              .filter(Boolean)
              .map((messageId) => lib.markTariffMessageRead(messageId))
          );
        } catch {
          // best-effort; ignore errors
        }
        // Refresh unread count after marking as read
        try {
          const unreadResp = await lib.getTariffMessagesUnreadCount(id);
          const unreadBody = unreadResp?.data;
          const unreadData = unreadBody?.data ?? unreadBody;
          setUnreadCount(unreadData?.unread_count ?? 0);
        } catch {
          setUnreadCount(0);
        }
      }
    } catch {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [id]);

  const loadHistory = useCallback(async () => {
    if (!id) return;
    setHistoryLoading(true);
    try {
      const resp = await lib.getTariffHistory(id);
      const body = resp?.data;
      const list =
        (Array.isArray(body?.data) ? body.data : null)
        ?? (Array.isArray(body?.history) ? body.history : null)
        ?? (Array.isArray(body?.items) ? body.items : null)
        ?? (Array.isArray(body?.results) ? body.results : null)
        ?? (Array.isArray(body?.data?.history) ? body.data.history : null)
        ?? (Array.isArray(body?.data?.items) ? body.data.items : null)
        ?? (Array.isArray(body) ? body : []);
      const rawList = Array.isArray(list) ? list : [];
      const safeList = rawList.map((h) => ({
        id: h.id ?? h.history_id ?? h.event_id,
        action_type: h.action_type ?? h.action ?? h.type ?? h.event_type,
        created_at: h.created_at ?? h.createdAt ?? h.timestamp ?? h.updated_at ?? h.updatedAt,
        actor: h.actor ?? h.user_id ?? h.actor_id ?? h.performed_by ?? h.user ?? h.email ?? '',
        description: h.description ?? h.message ?? h.details ?? h.summary ?? h.comment ?? '',
        old_values: h.old_values ?? h.oldValues ?? h.previous ?? h.before,
        new_values: h.new_values ?? h.newValues ?? h.changes ?? h.after,
      }));
      setHistory(safeList);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  // Fetch unread message count when tariff detail loads (guide §11.3)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    lib.getTariffMessagesUnreadCount(id)
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data?.data ?? resp?.data;
        setUnreadCount(data?.unread_count ?? 0);
      })
      .catch(() => { if (!cancelled) setUnreadCount(0); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (activeTab === 'messages') loadMessages();
  }, [activeTab, loadMessages]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await lib.getAdminTariffCategories();
      const body = res?.data;
      const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
      const options = list
        .map((c) => {
          const id = c.id ?? c.category_id ?? c.costable_category_id;
          const name = c.name ?? c.category_name ?? c.costable_category_name ?? c.plans_categories_name ?? (id != null ? `Category ${id}` : '');
          return id != null ? { value: Number(id), label: name } : null;
        })
        .filter(Boolean);
      setCategoryOptions(options);
    } catch {
      setCategoryOptions([]);
    }
  }, []);

  useEffect(() => {
    if (featureEnabled) loadCategories();
  }, [loadCategories]);

  const loadItems = useCallback(async () => {
    if (!id) return;
    setItemsLoading(true);
    try {
      const params = {
        page: itemsPage,
        page_size: itemsPageSize,
      };
      if (itemsSearch) params.search = itemsSearch;
      if (itemsCategoryId != null) params.category_id = itemsCategoryId;
      if (itemsNegotiationStatus) params.negotiation_status = itemsNegotiationStatus;

      const resp = await lib.getAdminTariffItems(id, params);
      const body = resp?.data;
      const data = body?.data ?? body;
      const list = data?.items ?? (Array.isArray(data) ? data : []);
      const safeList = Array.isArray(list) ? list : [];
      setItemsData(safeList);
      setItemsTotal(data?.total ?? safeList.length);
      if (typeof data?.page === 'number') {
        setItemsPage(data.page);
      }
      if (typeof data?.page_size === 'number') {
        setItemsPageSize(data.page_size);
      }
    } catch {
      // Fallback to embedded items if available
      const fallback = tariffRef.current?.items || [];
      setItemsData(fallback);
      setItemsTotal(fallback.length);
    } finally {
      setItemsLoading(false);
    }
  }, [id, itemsPage, itemsPageSize, itemsSearch, itemsCategoryId, itemsNegotiationStatus]);

  useEffect(() => {
    if (activeTab === 'items') {
      loadItems();
    }
  }, [activeTab, loadItems]);

  const itemsStats = useMemo(() => {
    const items = tariff?.items || [];
    let mapped = 0, unmapped = 0, proposed = 0, approved = 0, sumHmo = 0, hmoCount = 0;
    items.forEach((i) => {
      const ms = i.mapping_status || i.mappingStatus;
      if (ms === 'mapped') mapped += 1;
      if (ms === 'unmapped') unmapped += 1;
      const st = i.status || i.item_status;
      if (st === 'proposed' || st === 'pending') proposed += 1;
      if (st === 'approved') approved += 1;
      const hmo = i.counter_proposed_hmo_cost ?? i.proposed_hmo_cost ?? i.hmo_cost;
      if (typeof hmo === 'number') { sumHmo += hmo; hmoCount += 1; }
    });
    return {
      total: items.length,
      mapped,
      unmapped,
      proposed,
      approved,
      avgHmo: hmoCount ? sumHmo / hmoCount : null,
    };
  }, [tariff]);

  const handleDownloadCsv = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      const resp = await lib.downloadTariffCsv(id, tariff?.facility_id ?? tariff?.facility?.id);
      notification.success({ message: (resp?.message != null && resp.message !== '') ? resp.message : 'Tariff downloaded', placement: 'topRight' });
    } catch (e) {
      notification.error({
        message: getServerError(e, 'Unable to download tariff. Please try again.'),
        placement: 'topRight',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetUnderReview = async () => {
    setActionLoading(true);
    try {
      const resp = await lib.setTariffUnderReview(id);
      if (resp?.data?.error) throw new Error(resp.data.message);
      await loadTariff();
      notification.success({ message: getServerMessage(resp, 'Tariff set under review'), placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Action failed'), placement: 'topRight' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveClick = async () => {
    setActionLoading(true);
    setApprovalOptions([]);
    setAdoptPriceField(null);
    try {
      const resp = await lib.getTariffApprovalOptions(id);
      const body = resp?.data;
      if (body?.error) throw new Error(body?.message ?? 'Failed to load approval options');
      const data = body?.data ?? body;
      const opts = Array.isArray(data?.adoption_options) ? data.adoption_options : [];
      setApprovalOptions(opts);
      setAdoptPriceField(opts[0]?.field ?? null);
      setApproveModalOpen(true);
    } catch (e) {
      notification.error({ message: getServerError(e, 'Could not load approval options'), placement: 'topRight' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveConfirm = async () => {
    if (!adoptPriceField) {
      notification.warning({ message: 'Please choose which price to adopt', placement: 'topRight' });
      return;
    }
    setActionLoading(true);
    try {
      const resp = await lib.approveTariff(id, { adopt_price_field: adoptPriceField });
      if (resp?.data?.error) throw new Error(resp.data.message);
      setApproveModalOpen(false);
      await loadTariff();
      loadItems();
      notification.success({ message: getServerMessage(resp, 'Tariff approved'), placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Approve failed'), placement: 'topRight' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    rejectReasonRef.current = '';
    Modal.confirm({
      title: 'Reject tariff',
      content: (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Reason (required)</Text>
          <TextArea rows={3} onChange={(e) => { rejectReasonRef.current = e.target.value; }} placeholder="e.g. Pricing above market" />
        </div>
      ),
      okText: 'Reject',
      okButtonProps: { danger: true },
      onOk: async () => {
        const reason = rejectReasonRef.current?.trim();
        if (!reason) {
          notification.warning({ message: 'Please enter a reason', placement: 'topRight' });
          return Promise.reject();
        }
        setActionLoading(true);
        try {
          const resp = await lib.rejectTariff(id, reason);
          if (resp?.data?.error) throw new Error(resp.data.message);
          await loadTariff();
          notification.success({ message: getServerMessage(resp, 'Tariff rejected'), placement: 'topRight' });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Reject failed'), placement: 'topRight' });
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRequestRevision = () => {
    revisionMessageRef.current = '';
    Modal.confirm({
      title: 'Request revision',
      content: (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Message to facility (required)</Text>
          <TextArea rows={4} onChange={(e) => { revisionMessageRef.current = e.target.value; }} placeholder="e.g. Please reduce prices for items 10 and 15." />
        </div>
      ),
      okText: 'Send request',
      onOk: async () => {
        const message = revisionMessageRef.current?.trim();
        if (!message) {
          notification.warning({ message: 'Please enter a message', placement: 'topRight' });
          return Promise.reject();
        }
        setActionLoading(true);
        try {
          const resp = await lib.requestTariffRevision(id, { message });
          if (resp?.data?.error) throw new Error(resp.data.message);
          await loadTariff();
          notification.success({ message: getServerMessage(resp, 'Revision requested'), placement: 'topRight' });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Request failed'), placement: 'topRight' });
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const [counterProposeVisible, setCounterProposeVisible] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approvalOptions, setApprovalOptions] = useState([]);
  const [adoptPriceField, setAdoptPriceField] = useState(null);
  const handleCounterPropose = () => setCounterProposeVisible(true);
  const handleCounterProposeSubmit = async (adjustments, message) => {
    setActionLoading(true);
    try {
      const resp = await lib.counterProposeTariff(id, { adjustments, message: message?.trim() || undefined });
      if (resp?.data?.error) throw new Error(resp.data.message);
      await loadTariff();
      setCounterProposeVisible(false);
      loadItems();
      notification.success({ message: getServerMessage(resp, 'Counter-proposal sent'), placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Counter-propose failed'), placement: 'topRight' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCounterProposePercentage = async (payload) => {
    setActionLoading(true);
    try {
      const resp = await lib.counterProposeTariffPercentage(id, payload);
      if (resp?.data?.error) throw new Error(resp.data.message);
      await loadTariff();
      setCounterProposeVisible(false);
      loadItems();
      const serverMsg = getServerMessage(resp, 'Counter-propose by % applied');
      const summary = resp?.data?.data?.summary ?? resp?.data?.summary;
      const desc = summary ? `${summary.updated ?? 0} updated, ${summary.skipped ?? 0} skipped` : undefined;
      notification.success({ message: serverMsg, description: desc, placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Percentage apply failed'), placement: 'topRight' });
      throw e;
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = () => {
    if (!selectedRowKeys.length) return;
    Modal.confirm({
      title: 'Bulk approve',
      content: `Approve ${selectedRowKeys.length} selected item(s)?`,
      okText: 'Approve',
      onOk: async () => {
        setActionLoading(true);
        try {
          const resp = await lib.bulkApproveTariffItems(id, selectedRowKeys);
          if (resp?.data?.error) throw new Error(resp.data.message);
          await loadTariff();
          setSelectedRowKeys([]);
          notification.success({ message: getServerMessage(resp, 'Items approved'), placement: 'topRight' });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Bulk approve failed'), placement: 'topRight' });
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleAddItemSubmit = useCallback(async () => {
    if (!id) return;
    try {
      const values = await addItemForm.validateFields();
      setAddItemLoading(true);
      const body = {
        item_name: values.item_name?.trim(),
        ...(values.category_id != null && values.category_id !== '' && { category_id: Number(values.category_id) }),
        ...(values.facility_cost != null && values.facility_cost !== '' && { facility_cost: Number(values.facility_cost) }),
        ...(values.hmo_cost != null && values.hmo_cost !== '' && { hmo_cost: Number(values.hmo_cost) }),
        ...(values.item_brand?.trim() && { item_brand: values.item_brand.trim() }),
        ...(values.item_type?.trim() && { item_type: values.item_type.trim() }),
        ...(values.item_strength?.trim() && { item_strength: values.item_strength.trim() }),
        ...(values.item_presentation?.trim() && { item_presentation: values.item_presentation.trim() }),
        ...(values.standardized_item_id != null && values.standardized_item_id !== '' && { standardized_item_id: Number(values.standardized_item_id) }),
      };
      const resp = await lib.addAdminTariffItem(id, body);
      if (resp?.data?.error) throw new Error(resp.data.message ?? 'Failed to add item');
      setAddItemModalOpen(false);
      addItemForm.resetFields();
      await loadItems();
      notification.success({ message: getServerMessage(resp, 'Item added'), placement: 'topRight' });
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: getServerError(e, 'Failed to add item'), placement: 'topRight' });
    } finally {
      setAddItemLoading(false);
    }
  }, [id, addItemForm, loadItems]);

  const handleApproveItem = (item) => {
    const itemId = item.id ?? item.facility_tariff_item_id;
    if (!itemId) return;
    setActionLoading(true);
    return lib.approveTariffItem(itemId)
      .then(async (resp) => {
        if (resp?.data?.error) throw new Error(resp.data.message);
        await loadTariff();
        notification.success({ message: getServerMessage(resp, 'Item approved'), placement: 'topRight' });
      })
      .catch((e) => notification.error({ message: getServerError(e, 'Approve failed'), placement: 'topRight' }))
      .finally(() => setActionLoading(false));
  };

  const handleProposeCost = (item) => {
    const itemId = item.id ?? item.facility_tariff_item_id;
    if (!itemId) return;
    const initialCost = item.counter_proposed_hmo_cost ?? item.proposed_hmo_cost ?? item.facility_cost ?? 0;
    proposeCostRef.current = initialCost;
    proposeReasonRef.current = '';
    Modal.confirm({
      title: 'Propose HMO cost',
      content: (
        <div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Proposed HMO cost</Text>
              <InputNumber style={{ width: '100%', marginTop: 4 }} min={0} defaultValue={initialCost} onChange={(v) => { proposeCostRef.current = v; }} />
            </div>
            <div>
              <Text type="secondary">Reason (optional)</Text>
              <TextArea rows={2} onChange={(e) => { proposeReasonRef.current = e.target.value; }} placeholder="e.g. Market rate" />
            </div>
          </Space>
        </div>
      ),
      okText: 'Propose',
      onOk: async () => {
        const cost = proposeCostRef.current ?? initialCost;
        const reason = proposeReasonRef.current?.trim();
        setActionLoading(true);
        try {
          const resp = await lib.proposeTariffItemCost(itemId, { proposed_hmo_cost: cost, ...(reason && { reason }) });
          if (resp?.data?.error) throw new Error(resp.data.message);
          await loadTariff();
          notification.success({ message: getServerMessage(resp, 'Cost proposed'), placement: 'topRight' });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Propose failed'), placement: 'topRight' });
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleArchiveFacilityItem = (item) => {
    const itemId = item?.id ?? item?.facility_tariff_item_id;
    if (!id) return;
    if (!itemId) {
      // This facility's active tariff is LINKED to the HMO standard tariff (source
      // = standard), so this row has no facility_tariff_items row to archive — it's
      // read straight from the standard tariff (facility_tariff_item_id is null for
      // every line). Silently returning here used to leave the button looking dead:
      // no dialog, no error. Per-item archiving on a linked tariff isn't implemented
      // — doing it safely means every read path that prices this item (PA pricing,
      // facility portal search, checkin policy) also has to respect the exclusion,
      // and that's a larger change. Tell the admin the truth instead of pretending
      // the click did nothing, or worse, silently succeeding without hiding it
      // anywhere that actually prices claims.
      Modal.info({
        title: "Can't archive this item from here",
        content: (
          <div>
            <p>
              This facility's active tariff is <strong>linked</strong> to the HMO
              standard tariff, so its line items aren't stored per-facility — this
              row is read directly from the standard tariff.
            </p>
            <p>
              Per-item archiving only works on a facility-owned or copied tariff.
              To hide this item for this facility only, either archive it on the
              standard tariff (affects every facility using it) or switch this
              facility onto a facility-local copy of the tariff first.
            </p>
          </div>
        ),
      });
      return;
    }
    const linkedToStandard = item?.standardized_item_id != null && item.standardized_item_id !== '';
    Modal.confirm({
      title: 'Archive facility tariff item?',
      content: (
        <div>
          <p>
            Archive <strong>{item?.item_name ?? 'this item'}</strong> from this facility tariff?
          </p>
          <Text type="secondary">
            Facility-local only — hides the line on this facility tariff. The HMO standard tariff is not changed
            {linkedToStandard ? ', even though this line is linked to a standardized/standard-sourced item' : ''}.
          </Text>
        </div>
      ),
      okText: 'Archive from facility tariff',
      okButtonProps: { danger: true },
      onOk: async () => {
        setActionLoading(true);
        try {
          const resp = await lib.archiveAdminFacilityTariffItem(id, itemId);
          if (resp?.data?.error) throw new Error(resp.data.message ?? 'Failed to archive item');
          setItemDetailModalRecord(null);
          await loadItems();
          await loadTariff();
          notification.success({
            message: getServerMessage(resp, 'Item archived from facility tariff'),
            placement: 'topRight',
          });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Failed to archive item'), placement: 'topRight' });
          throw e;
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleSendMessage = async () => {
    if (!messageText?.trim()) return;
    setSendingMessage(true);
    try {
      const resp = await lib.sendTariffMessage(id, { message_type: 'query', message_text: messageText.trim() });
      if (resp?.data?.error) throw new Error(resp.data.message);
      setMessageText('');
      await loadMessages();
      notification.success({ message: getServerMessage(resp, 'Message sent'), placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Send failed'), placement: 'topRight' });
    } finally {
      setSendingMessage(false);
    }
  };

  // Standard tariff item layout (shared with StandardTariffDetail and the
  // facility portal): Item Name, Category, Strength, Presentation, Approved
  // Cost, Status, Actions. Facility cost, HMO proposals, code and brand are
  // not columns; they open with the item (row click → detail modal).
  const itemColumns = [
    { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', ellipsis: true, render: (v) => v ?? '—' },
    { title: 'Category', dataIndex: 'category_name', key: 'category', width: 140, ellipsis: true, render: (v, r) => v ?? r?.category ?? '—' },
    { title: 'Strength', dataIndex: 'item_strength', key: 'item_strength', width: 110, ellipsis: true, render: (v) => (v && String(v).trim()) || '—' },
    { title: 'Presentation', dataIndex: 'item_presentation', key: 'item_presentation', width: 130, ellipsis: true, render: (v) => (v && String(v).trim()) || '—' },
    { title: 'Approved Cost', dataIndex: 'unit_cost', key: 'unit_cost', width: 120, align: 'right', render: (v, r) => (v != null ? Number(v) : (r?.approved_cost != null ? Number(r.approved_cost) : '—')) },
    { title: 'Status', key: 'item_status', width: 110, render: (_, r) => <StatusTag status={r.status ?? r.negotiation_status} /> },
    {
      title: 'Actions',
      key: 'item_actions',
      width: 100,
      render: (_, record) => {
        const hasLocalRow = (record?.id ?? record?.facility_tariff_item_id) != null;
        return (
          <Tooltip
            title={hasLocalRow ? undefined : 'From the linked HMO standard tariff — click for how to hide it'}
          >
            <Button
              type="link"
              size="small"
              danger={hasLocalRow}
              onClick={(e) => {
                e.stopPropagation();
                handleArchiveFacilityItem(record);
              }}
            >
              Archive
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  if (!featureEnabled) {
    return (
      <div style={{ padding: 24 }}>
        <Button type="link" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>← Back</Button>
        <Title level={3}>Tariff Management (V2)</Title>
        <Alert type="info" message="Tariff module is currently disabled" description="The Tariff V2 feature flag is OFF." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          paddingBottom: 16,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => (facilityId ? navigate(`/facilities/${facilityId}/tariffs`) : navigate('/admin/tariffs'))}
          style={{ flexShrink: 0 }}
        >
          {facilityId ? 'Back to facility tariffs' : 'Back to Standard tariff'}
        </Button>
        <Divider type="vertical" style={{ margin: 0, height: 20 }} />
        <Typography.Text strong style={{ fontSize: 15 }}>
          {tariff ? (tariff.tariff_name || tariff.name || 'Tariff detail') : 'Tariff detail'}
        </Typography.Text>
        {hasUploadReport && uploadReportPath && (
          <>
            <Divider type="vertical" style={{ margin: 0, height: 20 }} />
            <Button
              type="link"
              onClick={async () => {
                try {
                  await lib.downloadFileWithAuth(uploadReportPath);
                  notification.success({
                    message: 'Upload report download started',
                    placement: 'topRight',
                  });
                } catch (e) {
                  notification.error({
                    message: 'Failed to download upload report',
                    description: e?.message || 'Please try again.',
                    placement: 'topRight',
                  });
                }
              }}
            >
              Download upload report
            </Button>
          </>
        )}
      </div>
      {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Error" description={error} />}

      <Spin spinning={loading && !tariff}>
        {tariff && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space align="baseline" style={{ justifyContent: 'space-between', width: '100%', flexWrap: 'wrap' }}>
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>{tariff.tariff_name || tariff.name || 'Tariff'}</Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {tariff.facility_name ?? tariff.facility?.name ?? 'Facility'} {tariff.facility_code ? `(${tariff.facility_code})` : ''}
                    </Text>
                  </div>
                  <Space size="small">
                    {isTariffArchived ? <Tag color="default">Archived</Tag> : null}
                    <StatusTag status={tariff.status} />
                  </Space>
                </Space>
                <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                  <Descriptions.Item label="Effective Date">
                    {tariff.effective_date || tariff.effectiveDate ? new Date(tariff.effective_date || tariff.effectiveDate).toLocaleDateString() : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Expiry Date">
                    {tariff.expiry_date || tariff.expiryDate ? new Date(tariff.expiry_date || tariff.expiryDate).toLocaleDateString() : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Updated">
                    {tariff.updated_at || tariff.updatedAt ? new Date(tariff.updated_at || tariff.updatedAt).toLocaleString() : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Items">{itemsStats.total ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Proposed / Approved">{itemsStats.total ? `${itemsStats.proposed}/${itemsStats.approved}` : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Avg HMO Cost">{itemsStats.avgHmo != null ? itemsStats.avgHmo.toFixed(2) : '—'}</Descriptions.Item>
                </Descriptions>
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Actions
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
                    <Space wrap size="small">
                      <Button type="primary" icon={<CheckOutlined />} loading={actionLoading} disabled={isTerminal} onClick={handleApproveClick}>
                        Approve
                      </Button>
                      <Button danger icon={<CloseOutlined />} loading={actionLoading} disabled={isTerminal} onClick={handleReject}>
                        Reject
                      </Button>
                    </Space>
                    <Space wrap size="small">
                      <Button icon={<EyeOutlined />} loading={actionLoading} disabled={isTerminal} onClick={handleSetUnderReview}>
                        Set Under Review
                      </Button>
                      <Button icon={<EditOutlined />} loading={actionLoading} disabled={isTerminal} onClick={handleRequestRevision}>
                        Request revision
                      </Button>
                      <Button icon={<EditOutlined />} loading={actionLoading} disabled={isTerminal} onClick={handleCounterPropose}>
                        Counter-propose
                      </Button>
                    </Space>
                    <Button icon={<DownloadOutlined />} loading={actionLoading} onClick={handleDownloadCsv}>
                      Download CSV
                    </Button>
                    <Space size="small">
                      <Button icon={<MessageOutlined />} onClick={() => setActiveTab('messages')}>
                        Messages
                      </Button>
                      <Button icon={<HistoryOutlined />} onClick={() => setActiveTab('history')}>
                        History
                      </Button>
                    </Space>
                    {isTariffArchived ? (
                      <Button icon={<UndoOutlined />} onClick={handleUnarchiveTariff}>
                        Restore tariff
                      </Button>
                    ) : (
                      <Button danger icon={<InboxOutlined />} onClick={() => setArchiveTariffModalOpen(true)}>
                        Archive tariff
                      </Button>
                    )}
                  </div>
                </div>
              </Space>
            </Card>

            <Modal
              title={`Archive ${tariff.tariff_name || tariff.name || 'tariff'}?`}
              open={archiveTariffModalOpen}
              onCancel={closeArchiveTariffModal}
              destroyOnClose
              footer={[
                <Button key="cancel" onClick={closeArchiveTariffModal} disabled={archivingTariff}>
                  Cancel
                </Button>,
                archiveTariffBlocking ? (
                  <Button key="force" type="primary" danger loading={archivingTariff} onClick={() => runArchiveTariff(true)}>
                    Archive anyway
                  </Button>
                ) : (
                  <Button key="archive" type="primary" danger loading={archivingTariff} onClick={() => runArchiveTariff(false)}>
                    Archive
                  </Button>
                ),
              ]}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text type="secondary">
                  Archived tariffs are hidden from tariff lists and pickers. This does not change the
                  tariff's approval status and does not affect any facility currently priced from it —
                  existing prices keep working exactly as they do today.
                </Text>
                {archiveTariffBlocking ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="This tariff is currently in use"
                    description={
                      archiveTariffBlocking.facilities.length > 0
                        ? `It's the active pricing source for: ${archiveTariffBlocking.facilities
                            .map((f) => f.facility_name || `Facility #${f.facility_id}`)
                            .join(', ')}. Archive anyway to proceed — those facilities keep their current prices; nothing switches automatically.`
                        : `It's the active pricing source for ${archiveTariffBlocking.active_pointer_count} facility assignment(s). Archive anyway to proceed — those facilities keep their current prices; nothing switches automatically.`
                    }
                  />
                ) : null}
                <Form layout="vertical">
                  <Form.Item label="Reason (optional)">
                    <TextArea
                      rows={2}
                      maxLength={500}
                      value={archiveTariffReason}
                      onChange={(e) => setArchiveTariffReason(e.target.value)}
                      placeholder="e.g. superseded by a new negotiated tariff"
                    />
                  </Form.Item>
                </Form>
              </Space>
            </Modal>

            <CounterProposeModal
              visible={counterProposeVisible}
              tariffId={id}
              loading={actionLoading}
              onClose={() => setCounterProposeVisible(false)}
              onSubmit={handleCounterProposeSubmit}
              onPercentageSubmit={handleCounterProposePercentage}
              categoryOptions={categoryOptions}
            />

            <Modal
              title="Approve tariff"
              open={approveModalOpen}
              onCancel={() => setApproveModalOpen(false)}
              onOk={handleApproveConfirm}
              okText="Approve"
              confirmLoading={actionLoading}
              okButtonProps={{ disabled: !adoptPriceField || approvalOptions.length === 0 }}
              destroyOnClose
            >
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary">Choose which price to adopt for all items. Items with no value for the selected field will stay pending.</Text>
              </div>
              {approvalOptions.length === 0 ? (
                <Text type="secondary">No price data available to adopt for this tariff.</Text>
              ) : (
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>Adopt price from</Text>
                  <Select
                    value={adoptPriceField}
                    onChange={setAdoptPriceField}
                    options={approvalOptions.map((o) => ({ value: o.field, label: o.label ?? o.field }))}
                    style={{ width: '100%' }}
                    placeholder="Select price field"
                  />
                </div>
              )}
            </Modal>

            <Modal
              title="Item details"
              open={!!itemDetailModalRecord}
              onCancel={() => setItemDetailModalRecord(null)}
              footer={[
                <Button key="close" onClick={() => setItemDetailModalRecord(null)}>Close</Button>,
                ...(itemDetailModalRecord ? (() => {
                  const itemStatus = itemDetailModalRecord.status ?? itemDetailModalRecord.item_status;
                  const canApprove = !isTerminal && (itemStatus === 'pending' || itemStatus === 'proposed');
                  return [
                    canApprove && (
                      <Button
                        key="approve"
                        type="primary"
                        loading={actionLoading}
                        onClick={() => {
                          const p = handleApproveItem(itemDetailModalRecord);
                          if (p && typeof p.then === 'function') {
                            p.then(() => { setItemDetailModalRecord(null); loadItems(); });
                          }
                        }}
                      >
                        Approve
                      </Button>
                    ),
                    <Button
                      key="propose"
                      disabled={isTerminal}
                      loading={actionLoading}
                      onClick={() => {
                        setItemDetailModalRecord(null);
                        handleProposeCost(itemDetailModalRecord);
                      }}
                    >
                      Propose cost
                    </Button>,
                    <Button
                      key="archive"
                      danger
                      loading={actionLoading}
                      onClick={() => handleArchiveFacilityItem(itemDetailModalRecord)}
                    >
                      Archive
                    </Button>,
                  ].filter(Boolean);
                })() : []),
              ]}
              width={520}
              destroyOnClose
            >
              {itemDetailModalRecord && (
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Item name">{itemDetailModalRecord.item_name ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Category">{itemDetailModalRecord.category_name ?? itemDetailModalRecord?.category ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Strength">{itemDetailModalRecord.item_strength ?? itemDetailModalRecord?.strength ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Presentation">{itemDetailModalRecord.item_presentation ?? itemDetailModalRecord?.presentation ?? itemDetailModalRecord?.form ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Type">{itemDetailModalRecord.item_type ?? itemDetailModalRecord.type ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Dosage">{itemDetailModalRecord.item_dosage ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Brand">{itemDetailModalRecord.item_brand ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Code">{itemDetailModalRecord.item_code ?? itemDetailModalRecord.facility_code ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Facility cost">{itemDetailModalRecord.facility_cost != null ? Number(itemDetailModalRecord.facility_cost) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="HMO proposed cost">{itemDetailModalRecord.proposed_hmo_cost != null ? Number(itemDetailModalRecord.proposed_hmo_cost) : (itemDetailModalRecord.hmo_cost != null ? Number(itemDetailModalRecord.hmo_cost) : '—')}</Descriptions.Item>
                  <Descriptions.Item label="Counter-proposed cost">{itemDetailModalRecord.counter_proposed_hmo_cost != null ? Number(itemDetailModalRecord.counter_proposed_hmo_cost) : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Approved Cost">
                    {itemDetailModalRecord.unit_cost != null ? Number(itemDetailModalRecord.unit_cost) : (itemDetailModalRecord?.approved_cost != null ? Number(itemDetailModalRecord.approved_cost) : '—')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mapping">
                    <Tag>{(itemDetailModalRecord.mapping_status ?? itemDetailModalRecord.mappingStatus) ?? '—'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Item status">
                    <StatusTag status={itemDetailModalRecord.status ?? itemDetailModalRecord.negotiation_status} />
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Modal>

            <Modal
              title="Add tariff item"
              open={addItemModalOpen}
              onCancel={() => { setAddItemModalOpen(false); addItemForm.resetFields(); }}
              footer={[
                <Button key="cancel" onClick={() => { setAddItemModalOpen(false); addItemForm.resetFields(); }}>Cancel</Button>,
                <Button key="submit" type="primary" loading={addItemLoading} onClick={handleAddItemSubmit}>Add item</Button>,
              ]}
              width={480}
              destroyOnClose
            >
              <Form form={addItemForm} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item name="item_name" label="Item name" rules={[{ required: true, message: 'Item name is required' }]}>
                  <Input placeholder="e.g. Paracetamol 500mg" />
                </Form.Item>
                <Form.Item name="category_id" label="Category">
                  <Select allowClear placeholder="Select category" options={categoryOptions} />
                </Form.Item>
                <Form.Item name="facility_cost" label="Facility cost">
                  <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
                </Form.Item>
                <Form.Item name="hmo_cost" label="HMO cost">
                  <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="Optional" />
                </Form.Item>
                <Form.Item name="item_brand" label="Brand">
                  <Input placeholder="Optional" />
                </Form.Item>
                <Form.Item name="item_type" label="Type">
                  <Input placeholder="Optional" />
                </Form.Item>
                <Form.Item name="item_strength" label="Strength">
                  <Input placeholder="Optional" />
                </Form.Item>
                <Form.Item name="item_presentation" label="Presentation">
                  <Input placeholder="Optional" />
                </Form.Item>
                <Form.Item name="standardized_item_id" label="Standardized item ID (costable_items.id for PA pricing)">
                  <InputNumber min={1} integer style={{ width: '100%' }} placeholder="Optional — links to costable item" />
                </Form.Item>
              </Form>
            </Modal>

            <Card>
              <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
                {
                  key: 'items',
                  label: 'Items',
                  children: (
                    <div>
                      <Space style={{ marginBottom: 16 }} wrap>
                        <Input.Search
                          allowClear
                          placeholder="Search items (name, code, brand)…"
                          onSearch={(value) => {
                            setItemsPage(1);
                            setItemsSearch(value.trim());
                          }}
                          style={{ maxWidth: 260 }}
                        />
                        <Select
                          allowClear
                          placeholder="Negotiation status"
                          value={itemsNegotiationStatus}
                          onChange={(value) => {
                            setItemsPage(1);
                            setItemsNegotiationStatus(value || undefined);
                          }}
                          style={{ minWidth: 200 }}
                          options={[
                            { value: 'pending', label: 'Pending' },
                            { value: 'pending_negotiation', label: 'Pending negotiation (POA)' },
                            { value: 'approved', label: 'Approved' },
                            { value: 'proposed', label: 'Proposed by HMO' },
                          ]}
                        />
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddItemModalOpen(true)}>
                          Add item
                        </Button>
                      </Space>
                      {selectedRowKeys.length > 0 && (
                        <Space style={{ marginBottom: 16 }}>
                          <Button type="primary" loading={actionLoading} disabled={isTerminal} onClick={handleBulkApprove}>
                            Bulk approve ({selectedRowKeys.length})
                          </Button>
                          <Button onClick={() => setSelectedRowKeys([])}>Clear selection</Button>
                        </Space>
                      )}
                      <Table
                        rowSelection={!isTerminal ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
                        rowKey={getTariffItemRowKey}
                        dataSource={itemsData}
                        columns={itemColumns}
                        loading={itemsLoading}
                        onRow={(record) => ({
                          onClick: (e) => {
                            if (e.target?.closest?.('.ant-table-selection-column')) return;
                            setItemDetailModalRecord(record);
                          },
                          style: { cursor: 'pointer' },
                        })}
                        pagination={{
                          current: itemsPage,
                          pageSize: itemsPageSize,
                          total: itemsTotal,
                          showSizeChanger: true,
                          pageSizeOptions: ['20', '50', '100'],
                          showTotal: (total) => `Total ${total} item(s)`,
                          onChange: (page, pageSize) => {
                            setItemsPage(page);
                            setItemsPageSize(pageSize || itemsPageSize);
                          },
                        }}
                        size="small"
                        locale={{ emptyText: 'No items' }}
                      />
                    </div>
                  ),
                },
                {
                  key: 'messages',
                  label: unreadCount ? `Messages (${unreadCount})` : 'Messages',
                  children: (
                    <div>
                      <Spin spinning={messagesLoading}>
                        {messages.length === 0 && !messagesLoading ? (
                          <Empty description="No messages" />
                        ) : (
                          <div style={{ maxHeight: 320, overflow: 'auto', marginBottom: 16 }}>
                            {messages.map((m, idx) => (
                              <div
                                key={m.id ?? idx}
                                style={{
                                  padding: 8,
                                  marginBottom: 8,
                                  background: (m.sender === 'hmo' || m.source === 'hmo') ? '#e6f7ff' : '#f6ffed',
                                  borderRadius: 4,
                                }}
                              >
                                <Text strong>{m.sender ?? m.source ?? 'Unknown'}</Text>
                                <Text type="secondary" style={{ marginLeft: 8 }}>{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</Text>
                                <div style={{ marginTop: 4 }}>{m.message_text ?? m.text ?? m.content ?? '—'}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Spin>
                      <Space.Compact style={{ width: '100%' }}>
                        <TextArea rows={2} value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="Type a message..." />
                        <Button type="primary" loading={sendingMessage} disabled={!messageText?.trim()} onClick={handleSendMessage}>Send</Button>
                      </Space.Compact>
                    </div>
                  ),
                },
                {
                  key: 'history',
                  label: 'History',
                  children: (
                    <Spin spinning={historyLoading}>
                      {history.length === 0 && !historyLoading ? (
                        <Empty description="No history" />
                      ) : (
                        <Collapse
                          items={history.map((h, idx) => ({
                            key: h.id ?? idx,
                            label: (
                              <Text strong>{getHistoryStoryLabel(h)}</Text>
                            ),
                            children: (
                              <div style={{ padding: '8px 0' }}>
                                <div style={{ marginBottom: 12 }}>
                                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Story</Text>
                                  <div style={{ padding: 10, background: '#fafafa', borderRadius: 4 }}>
                                    {getHistoryStory(h)}
                                  </div>
                                </div>
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>Item data (preview)</Text>
                                  <pre style={{ margin: 0, fontSize: 11, background: '#f5f5f5', padding: 10, borderRadius: 4, overflow: 'auto', maxHeight: 240 }}>
                                    {JSON.stringify({ action_type: h.action_type, action: h.action, created_at: h.created_at, actor: h.actor, description: h.description, old_values: h.old_values, new_values: h.new_values }, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            ),
                          }))}
                        />
                      )}
                    </Spin>
                  ),
                },
              ]} />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
};

export default AdminTariffDetail;
